<template>
  <div class="smart-dashboard">
    <!-- 二级 Tab 切换 -->
    <div class="sub-tabs">
      <div
        v-for="item in subTabs"
        :key="item.key"
        class="sub-tab-item"
        :class="{ active: activeSubTab === item.key }"
        @click="activeSubTab = item.key">
        <i :class="item.icon"></i>
        <span>{{ item.label }}</span>
      </div>
    </div>

    <!-- ① 企业多维透视 -->
    <template v-if="activeSubTab === 'perspective'">
    <!-- 查询条件区域 -->
    <div class="query-panel">
      <div class="query-header">
        <div class="query-title">
          <i class="el-icon-s-operation"></i>
          <span>查询条件</span>
          <span class="required-tip">企业名称必填</span>
        </div>
        <div class="query-actions">
          <el-button size="small" @click="handleReset">
            <i class="el-icon-refresh-left"></i>
            重置查询条件
          </el-button>
          <el-button size="small" type="primary" :loading="detailLoading" @click="handleQuery">
            <i class="el-icon-search"></i>
            查询
          </el-button>
        </div>
      </div>
      <div class="query-form">
        <div class="form-item">
          <label class="form-label">
            <span class="required">*</span>企业名称
          </label>
          <el-autocomplete
            v-model="queryForm.companyName"
            class="form-input"
            placeholder="请输入要透视的企业名称"
            :fetch-suggestions="handleSearchSuggestions"
            :trigger-on-focus="false"
            :debounce="300"
            clearable
            @select="handleSelectEnterprise"
            @clear="handleClearResult">
            <template slot-scope="{ item }">
              <div class="suggestion-item">
                <span class="suggestion-main">
                  <span class="suggestion-name" v-html="item.enterpriseName"></span>
                  <span class="suggestion-region" v-if="item.region">{{ item.region }}</span>
                </span>
                <span class="suggestion-credit" v-if="item.creditCode">{{ item.creditCode }}</span>
              </div>
            </template>
          </el-autocomplete>
        </div>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-area" v-loading="detailLoading">
      <!-- 空状态 -->
      <div class="empty-content" v-if="!enterpriseBasicInfo">
        <div class="empty-icon-wrapper">
          <div class="empty-icon">
            <svg viewBox="0 0 1024 1024" width="80" height="80">
              <path d="M128 64h768a64 64 0 0 1 64 64v768a64 64 0 0 1-64 64H128a64 64 0 0 1-64-64V128a64 64 0 0 1 64-64z m32 96v640h704V160H160z m96 160h192v192H256V320z m320 0h192v192H576V320z m-320 320h192v96H256v-96z m320 0h192v96H576v-96z" fill="#fff"/>
            </svg>
          </div>
        </div>
        <div class="empty-title">{{ currentSubTabLabel }}</div>
        <div class="empty-desc">
          输入企业名称后，可查看以下洞察维度：
          <span class="dim-item">企业实力</span>
          <span class="dim-sep">|</span>
          <span class="dim-item">经营情况</span>
          <span class="dim-sep">|</span>
          <span class="dim-item">商业机会</span>
        </div>
      </div>

      <!-- 企业信息展示 -->
      <div class="enterprise-info" v-if="enterpriseBasicInfo">
        <!-- 企业头部卡片 -->
        <div class="ent-header">
          <div class="ent-logo">
            <i class="el-icon-office-building"></i>
          </div>
          <div class="ent-head-main">
            <div class="ent-name">{{ enterpriseBasicInfo.enterpriseName }}</div>
            <div class="ent-sub" v-if="enterpriseBasicInfo.industryName">{{ enterpriseBasicInfo.industryName }}</div>
          </div>
          <div class="ent-status-tag" v-if="enterpriseBasicInfo.enterpriseStatus">
            <i class="el-icon-success"></i>{{ enterpriseBasicInfo.enterpriseStatus }}
          </div>
          <!-- AI 速读：针对整家企业（工商 + 股东）生成结构化分析 -->
          <div class="ent-actions">
            <el-button
              size="small"
              type="primary"
              icon="el-icon-magic-stick"
              :loading="aiBriefLoading"
              :disabled="!enterpriseBasicInfo.enterpriseName"
              @click="handleEnterpriseBriefAi">
              AI 速读
            </el-button>
          </div>
        </div>

        <!-- 卡片数据 -->
        <div class="stat-cards">
          <div class="stat-card" v-for="card in statCards" :key="card.label">
            <div class="stat-label">{{ card.label }}</div>
            <div class="stat-value">{{ card.value }}</div>
            <div class="stat-foot">{{ card.foot }}</div>
          </div>
        </div>

        <!-- 工商档案 + 股东结构 -->
        <div class="detail-row">
          <!-- 工商档案 -->
          <div class="info-section archive-section">
            <div class="section-header">
              <i class="el-icon-document-copy"></i>
              <span>工商档案</span>
              <span class="alive-tag" v-if="enterpriseBasicInfo.enterpriseStatus">
                <i class="dot"></i>{{ enterpriseBasicInfo.enterpriseStatus }}
              </span>
            </div>
            <div class="info-cell" v-if="enterpriseBasicInfo.creditCode">
              <span class="info-label">统一社会信用代码</span>
              <span class="info-value">{{ enterpriseBasicInfo.creditCode }}</span>
            </div>
            <div class="info-grid-two">
              <div class="info-cell">
                <span class="info-label">企业类型</span>
                <span class="info-value">{{ enterpriseBasicInfo.enterpriseType || '-' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">法定代表人</span>
                <span class="info-value">{{ enterpriseBasicInfo.legalPersonName || '-' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">注册资本</span>
                <span class="info-value">{{ enterpriseBasicInfo.registeredCapital || '-' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">参保人数</span>
                <span class="info-value">{{ enterpriseBasicInfo.insuredCount || '-' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">成立日期</span>
                <span class="info-value">{{ enterpriseBasicInfo.establishDate || '-' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">所属行业</span>
                <span class="info-value">{{ enterpriseBasicInfo.industryName || '-' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">登记机关</span>
                <span class="info-value">{{ enterpriseBasicInfo.issuingAuthority || '-' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">营业期限</span>
                <span class="info-value">{{ enterpriseBasicInfo.businessPeriod || '-' }}</span>
              </div>
            </div>
            <div class="info-cell" v-if="enterpriseBasicInfo.registerAddress">
              <span class="info-label">注册地址</span>
              <span class="info-value">{{ enterpriseBasicInfo.registerAddress }}</span>
            </div>
            <div class="info-cell" v-if="enterpriseBasicInfo.enterpriseProfile">
              <span class="info-label">企业简介</span>
              <span class="info-value">{{ enterpriseBasicInfo.enterpriseProfile }}</span>
            </div>
            <div class="info-cell" v-if="historyNames.length">
              <span class="info-label">曾用名</span>
              <span class="info-value">
                <span class="history-tag" v-for="(name, i) in historyNames" :key="i">{{ name }}</span>
              </span>
            </div>
            <div class="info-cell" v-if="enterpriseBasicInfo.businessScope">
              <span class="info-label">经营范围</span>
              <span class="info-value">{{ enterpriseBasicInfo.businessScope }}</span>
            </div>
          </div>

          <!-- 股东结构 -->
          <div class="info-section shareholder-section">
            <div class="section-header">
              <i class="el-icon-user"></i>
              <span>股东结构</span>
            </div>
            <div class="section-tip">
              共{{ shareholderTotal }}名股东，展示前{{ shareholders.length }}名；持股比例以公开披露口径为准。
            </div>
            <el-table :data="shareholders" border size="small" style="width: 100%" v-loading="shareholderLoading">
              <el-table-column label="排名" width="55" align="center">
                <template slot-scope="scope">{{ (shareholderPageNo - 1) * 10 + scope.$index + 1 }}</template>
              </el-table-column>
              <el-table-column prop="shareholderName" label="股东名称" min-width="180" show-overflow-tooltip />
              <el-table-column label="持股比例" width="90" align="right">
                <template slot-scope="scope">
                  <span class="ratio-text">{{ scope.row.shareholdingRatio || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="planContributionAmount" label="认缴金额" width="100" show-overflow-tooltip />
              <el-table-column label="认缴日期" width="100">
                <template slot-scope="scope">{{ formatDate(scope.row.planContributionDate) }}</template>
              </el-table-column>
            </el-table>
            <el-pagination
              v-if="shareholderTotal > 0"
              small
              layout="total, prev, pager, next"
              :total="shareholderTotal"
              :page-size="10"
              :current-page.sync="shareholderPageNo"
              @current-change="fetchShareholders"
              class="shareholder-pager" />
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- ② 招投标查询 -->
    <template v-else-if="activeSubTab === 'bidding'">
      <!-- 查询条件区域 -->
      <div class="query-panel">
        <div class="query-header">
          <div class="query-title">
            <i class="el-icon-s-operation"></i>
            <span>查询条件</span>
            <span class="required-tip">可按企业、地域、公告类型组合检索</span>
          </div>
          <div class="query-actions">
            <el-button size="small" @click="handleBidReset">
              <i class="el-icon-refresh-left"></i>
              重置查询条件
            </el-button>
            <el-button size="small" type="primary" :loading="bidResolving" @click="handleBidQuery">
              <i class="el-icon-search"></i>
              查询
            </el-button>
          </div>
        </div>
        <div class="bid-form">
          <div class="form-item">
            <label class="form-label">企业名称</label>
            <el-autocomplete
              v-model="bidForm.companyName"
              placeholder="不填代表不限云企业"
              :fetch-suggestions="handleSearchSuggestions"
              :trigger-on-focus="false"
              :debounce="300"
              clearable
              @select="handleBidSelect"
              @clear="handleBidClear" />
          </div>
          <div class="form-item">
            <label class="form-label">地域</label>
            <el-select v-model="bidForm.region" placeholder="不限">
              <el-option label="不限" value="" />
            </el-select>
          </div>
          <div class="form-item">
            <label class="form-label">标题关键词</label>
            <el-input v-model="bidForm.titleKeyword" placeholder="模糊匹配公告标题" clearable />
          </div>
          <div class="form-item">
            <label class="form-label">项目名称关键词</label>
            <el-input v-model="bidForm.projectKeyword" placeholder="模糊匹配项目名称" clearable />
          </div>
          <div class="form-item">
            <label class="form-label">招标类型</label>
            <el-select v-model="bidForm.bidType" placeholder="全部">
              <el-option label="全部" value="all" />
              <el-option label="招标方" :value="100" />
              <el-option label="投标方" :value="200" />
              <el-option label="代理方" :value="300" />
              <el-option label="被提及" :value="400" />
            </el-select>
          </div>
          <div class="form-item bid-date-item">
            <label class="form-label">公告日期区间</label>
            <div class="date-range">
              <el-date-picker
                v-model="bidForm.startDate"
                type="date"
                placeholder="年 /月 /日"
                value-format="timestamp" />
              <span class="date-sep">至</span>
              <el-date-picker
                v-model="bidForm.endDate"
                type="date"
                placeholder="年 /月 /日"
                value-format="timestamp" />
            </div>
          </div>
        </div>
      </div>

      <!-- 招投标跟踪 -->
      <div class="content-area bid-content" v-loading="bidLoading">
      <template v-if="!bidDetailView">
        <div class="bid-track-header">
          <div class="track-title">
            <i class="el-icon-data-analysis"></i>
            <span>招投标跟踪</span>
          </div>
          <div class="track-actions">
          </div>
        </div>

        <!-- 空状态 -->
        <div class="empty-content bid-empty" v-if="!bidQueried">
          <div class="empty-title">{{ currentSubTabLabel }}</div>
          <div class="empty-desc">输入企业名称后，可查询该企业的招投标公开记录</div>
        </div>

        <!-- 招投标记录卡片 -->
        <div class="bid-record-card" v-if="bidQueried">
          <div class="bid-record-head">
            <div class="bid-record-icon">
              <i class="el-icon-collection-tag"></i>
            </div>
            <div class="bid-record-info">
              <div class="bid-record-title">招投标记录</div>
              <div class="bid-record-sub">共 {{ bidTotal }} 条公开公告记录，点击公告标题可查看详情。</div>
            </div>
            <div class="bid-record-tags">
              <span class="bid-tag bid-tag-green">{{ bidBuiltCount }}个拟建/跨标项目</span>
              <span class="bid-tag bid-tag-orange">{{ bidTypeLabel }}类型</span>
              <el-button
                size="mini"
                type="primary"
                icon="el-icon-data-analysis"
                :loading="bidBriefLoading"
                @click="handleBidBriefAi"
                style="margin-left: 8px;">AI 分析</el-button>
            </div>
          </div>
          <el-table :data="displayBids" style="width: 100%" empty-text="暂无数据">
            <el-table-column label="公告标题" min-width="260">
              <template slot-scope="scope">
                <span class="bid-title-link" @click="handleBidDrill(scope.row)">
                  {{ stripHtml(scope.row.title) || '-' }}<i class="el-icon-arrow-right"></i>
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="owner" label="采购单位" min-width="150" show-overflow-tooltip />
            <el-table-column label="公告状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag :type="bidStatusTag(scope.row.type)" size="small">{{ scope.row.type || '-' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="role" label="企业角色" width="130" show-overflow-tooltip />
            <el-table-column label="发布时间" width="110">
              <template slot-scope="scope">{{ formatDate(scope.row.publishTime) }}</template>
            </el-table-column>
            <el-table-column label="发布渠道" min-width="130" show-overflow-tooltip>
              <template slot-scope="scope">{{ (scope.row.comeFromArr || []).join('、') || '-' }}</template>
            </el-table-column>
          </el-table>
          <el-pagination
            v-if="bidTotal > 0"
            small
            layout="total, prev, pager, next"
            :total="bidTotal"
            :page-size="10"
            :current-page.sync="bidPageNo"
            @current-change="fetchBids"
            class="bid-pager" />
        </div>
      </template>

      <!-- 公告详情视图（下钻） -->
      <div class="bid-detail" v-else v-loading="bidDetailLoading">
        <template v-if="bidDetail">
          <!-- 详情头部 -->
          <div class="detail-back-row">
            <el-button size="small" icon="el-icon-arrow-left" @click="bidDetailView = false">返回列表</el-button>
          </div>
          <div class="detail-header">
            <div class="detail-title">{{ stripHtml(bidDetail.title) || '-' }}</div>
            <div class="detail-tags">
              <el-tag v-if="bidDetail.typeSub" size="small" :type="bidStatusTag(bidDetail.typeSub)">{{ bidDetail.typeSub }}</el-tag>
              <el-tag v-if="bidDetail.role" size="small" type="info">{{ bidDetail.role }}</el-tag>
            </div>
            <div class="detail-meta">
              <span><i class="el-icon-time"></i>发布时间：{{ formatDate(bidDetail.pubtime) }}</span>
              <span><i class="el-icon-document"></i>项目编号：{{ bidDetail.projectNum || '-' }}</span>
              <span><i class="el-icon-location-outline"></i>所属地区：{{ bidDetail.region || '-' }}</span>
              <span><i class="el-icon-link"></i>发布渠道：{{ (bidDetail.comeFromArr || []).join('、') || '-' }}</span>
            </div>
          </div>

          <!-- 关键金额 -->
          <div class="detail-amount-row">
            <div class="amount-card">
              <div class="amount-label">项目预算金额</div>
              <div class="amount-value">{{ formatMoney(bidDetail.budget) }}</div>
            </div>
            <div class="amount-card amount-card-highlight">
              <div class="amount-label">中标成交金额</div>
              <div class="amount-value">{{ formatMoney(bidDetail.bidPrice) }}</div>
              <div class="amount-sub">中标单位：{{ bidDetail.bidUnit || '-' }}</div>
            </div>
            <div class="amount-card">
              <div class="amount-label">项目截止日期</div>
              <div class="amount-value">{{ formatDate(bidDetail.blockTime) }}</div>
            </div>
          </div>

          <!-- 项目信息 -->
          <div class="detail-section">
            <div class="section-header"><i class="el-icon-tickets"></i><span>项目信息</span></div>
            <div class="info-grid-two">
              <div class="info-cell"><span class="info-label">项目名称</span><span class="info-value">{{ bidDetail.projectName || '-' }}</span></div>
              <div class="info-cell"><span class="info-label">项目编号</span><span class="info-value">{{ bidDetail.projectNum || '-' }}</span></div>
              <div class="info-cell"><span class="info-label">发布日期</span><span class="info-value">{{ formatDate(bidDetail.pubtime) }}</span></div>
              <div class="info-cell"><span class="info-label">截止日期</span><span class="info-value">{{ formatDate(bidDetail.blockTime) }}</span></div>
            </div>
          </div>

          <!-- 采购 / 代理 信息 -->
          <div class="detail-two-col">
            <div class="detail-section">
              <div class="section-header"><i class="el-icon-office-building"></i><span>采购单位</span></div>
              <div class="info-cell"><span class="info-label">单位名称</span><span class="info-value">{{ bidDetail.owner || '-' }}</span></div>
              <div class="info-cell"><span class="info-label">联系人</span><span class="info-value">{{ bidDetail.ownerPerson || '-' }}</span></div>
              <div class="info-cell"><span class="info-label">联系电话</span><span class="info-value">{{ bidDetail.ownerPhone || '-' }}</span></div>
              <div class="info-cell"><span class="info-label">单位地址</span><span class="info-value">{{ bidDetail.ownerAddress || '-' }}</span></div>
            </div>
            <div class="detail-section">
              <div class="section-header"><i class="el-icon-s-cooperation"></i><span>代理机构</span></div>
              <div class="info-cell"><span class="info-label">机构名称</span><span class="info-value">{{ bidDetail.agency || '-' }}</span></div>
              <div class="info-cell"><span class="info-label">联系电话</span><span class="info-value">{{ bidDetail.agencyPhone || '-' }}</span></div>
              <div class="info-cell"><span class="info-label">机构地址</span><span class="info-value">{{ bidDetail.agencyAddress || '-' }}</span></div>
              <div class="info-cell">
                <span class="info-label">原文链接</span>
                <span class="info-value">
                  <template v-if="(bidDetail.comeFromInfoArr || []).some(x => x.comeFromUrl)">
                    <template v-for="(item, i) in bidDetail.comeFromInfoArr">
                      <a v-if="item.comeFromUrl" :key="i"
                         class="bid-source-link" :href="item.comeFromUrl" target="_blank" rel="noopener">
                        {{ item.comeFromName || '查看原文' }}<i class="el-icon-top-right"></i>
                      </a>
                    </template>
                  </template>
                  <span v-else>暂无原文链接</span>
                </span>
              </div>
            </div>
          </div>

          <!-- 公告正文 -->
          <div class="detail-section" v-if="bidDetail.text">
            <div class="section-header"><i class="el-icon-document"></i><span>公告正文</span></div>
            <div class="detail-text">{{ bidDetail.text }}</div>
          </div>
        </template>
      </div>
      </div>
    </template>

    <!-- ③ 企业画像筛选 -->
    <template v-else-if="activeSubTab === 'portrait'">
      <!-- 筛选条件区 -->
      <div class="query-panel portrait-panel">
        <div class="query-header">
          <div class="query-title">
            <i class="el-icon-s-operation"></i>
            <span>高级筛选</span>
            <span class="required-tip">支持多组条件组合，组间用"满足下列"逻辑连接</span>
          </div>
          <div class="query-actions">
            <el-button size="small" @click="handlePortraitReset">
              <i class="el-icon-refresh-left"></i> 重置
            </el-button>
            <el-button size="small" type="primary" :loading="portraitLoading" @click="handlePortraitQuery">
              <i class="el-icon-search"></i> 筛选
            </el-button>
          </div>
        </div>

        <!-- 条件组（根组） -->
        <div class="cond-groups">
          <div v-for="(group, gIdx) in portraitTree.groups" :key="group.id"
            class="cond-group" :class="{ 'cond-group-root': gIdx === 0 }">
            <!-- 组头 + 组逻辑 -->
            <div class="cond-group-header">
              <div class="cond-group-title">
                <i class="el-icon-folder-opened"></i>
                <span v-if="gIdx === 0">满足下列</span>
                <span v-else>且 满足下列</span>
              </div>
              <el-select v-model="group.logic" size="mini" class="cond-logic-select">
                <el-option label="所有（AND）" value="all" />
                <el-option label="任一（OR）" value="any" />
              </el-select>
              <span class="cond-colon">条件：</span>
              <el-button v-if="gIdx > 0" size="mini" type="text" icon="el-icon-delete"
                class="cond-group-del" @click="removeGroup(gIdx)">删除组</el-button>
            </div>

            <!-- 条件行 -->
            <div class="cond-rows">
              <div v-for="(cond, cIdx) in group.conditions" :key="cond.id" class="cond-row">
                <el-select v-model="cond.field" size="mini" class="cond-field-select"
                  placeholder="选择字段" @change="onFieldChange(cond)">
                  <el-option v-for="f in portraitFieldSchema" :key="f.key"
                    :label="f.label" :value="f.key" />
                </el-select>
                <el-select v-model="cond.op" size="mini" class="cond-op-select">
                  <el-option v-for="op in fieldOps(cond.field)" :key="op.value"
                    :label="op.label" :value="op.value" />
                </el-select>
                <!-- 值控件：根据 field type 动态切换 -->
                <template>
                  <el-input v-if="fieldType(cond.field) === 'text'"
                    v-model="cond.value" size="mini" class="cond-value-input"
                    :placeholder="cond.op === 'eq' ? '精确匹配' : '输入关键词'" clearable />
                  <el-select v-else-if="fieldType(cond.field) === 'select'"
                    v-model="cond.value" size="mini" class="cond-value-input"
                    :multiple="isMultiple(cond.field)" collapse-tags filterable
                    :placeholder="'选择' + fieldLabel(cond.field) + '值'" clearable>
                    <el-option v-for="opt in fieldOptions(cond.field)" :key="opt"
                      :label="opt" :value="opt" />
                  </el-select>
                  <template v-else-if="fieldType(cond.field) === 'number-range'">
                    <div class="cond-range-wrap">
                      <el-input v-model="cond.valueMin" size="mini" class="cond-range-input"
                        placeholder="最小" type="number" />
                      <span class="range-sep-tile">至</span>
                      <el-input v-model="cond.valueMax" size="mini" class="cond-range-input"
                        placeholder="最大" type="number" />
                      <span class="cond-range-unit">万元</span>
                    </div>
                  </template>
                  <template v-else-if="fieldType(cond.field) === 'date-range'">
                    <div class="cond-range-wrap">
                      <el-date-picker v-model="cond.valueMin" type="date" size="mini"
                        placeholder="开始日期" value-format="yyyy-MM-dd" class="cond-date-min" />
                      <span class="range-sep-tile">至</span>
                      <el-date-picker v-model="cond.valueMax" type="date" size="mini"
                        placeholder="结束日期" value-format="yyyy-MM-dd" class="cond-date-max" />
                    </div>
                  </template>
                </template>
                <el-button size="mini" type="text" icon="el-icon-delete" class="cond-row-del"
                  @click="removeCond(gIdx, cIdx)" />
              </div>

              <!-- 行内添加条件 -->
              <div class="cond-row-add">
                <el-button size="mini" type="text" icon="el-icon-plus" @click="addCond(gIdx)">
                  添加条件
                </el-button>
              </div>
            </div>
          </div>

          <!-- 添加条件组 -->
          <div class="cond-add-group">
            <el-button size="mini" icon="el-icon-plus" plain @click="addGroup()">
              添加条件组
            </el-button>
          </div>
        </div>

      </div>

      <!-- 结果列表 -->
      <div class="content-area portrait-content" v-loading="portraitLoading">
        <!-- 空状态 -->
        <div class="empty-content" v-if="!portraitQueried">
          <div class="empty-icon-wrapper">
            <div class="empty-icon">
              <svg viewBox="0 0 1024 1024" width="80" height="80">
                <path d="M128 64h768a64 64 0 0 1 64 64v768a64 64 0 0 1-64 64H128a64 64 0 0 1-64-64V128a64 64 0 0 1 64-64z m32 96v640h704V160H160z m96 160h192v192H256V320z m320 0h192v192H576V320z m-320 320h192v96H256v-96z m320 0h192v96H576v-96z" fill="#fff"/>
              </svg>
            </div>
          </div>
          <div class="empty-title">企业画像筛选</div>
          <div class="empty-desc">添加条件组和条件行，构造复杂筛选规则</div>
        </div>

        <!-- 结果表格 -->
        <div class="portrait-result" v-if="portraitQueried">
          <div class="portrait-result-header">
            <div class="result-count">
              <i class="el-icon-s-data"></i>
              <span>共筛选出 <strong>{{ portraitTotal }}</strong> 家企业</span>
            </div>
            <el-button size="small" @click="handlePortraitExport">
              <i class="el-icon-download"></i> 导出
            </el-button>
          </div>
          <el-table :data="portraitList" border size="small" style="width: 100%" empty-text="暂无符合条件的企业">
            <el-table-column label="企业名称" min-width="200" show-overflow-tooltip>
              <template slot-scope="scope">
                <span class="portrait-name-link" @click="handlePortraitDrill(scope.row)">
                  {{ scope.row.enterpriseName || '-' }}
                  <i class="el-icon-arrow-right"></i>
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="legalPersonName" label="法定代表人" width="120" show-overflow-tooltip />
            <el-table-column prop="registeredCapital" label="注册资本" width="140" show-overflow-tooltip />
            <el-table-column label="成立日期" width="110">
              <template slot-scope="scope">{{ scope.row.establishDate || '-' }}</template>
            </el-table-column>
            <el-table-column prop="enterpriseStatus" label="经营状态" width="80" align="center">
              <template slot-scope="scope">
                <el-tag :type="portraitStatusTag(scope.row.enterpriseStatus)" size="mini">
                  {{ scope.row.enterpriseStatus || '-' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="enterpriseType" label="企业类型" min-width="120" show-overflow-tooltip />
            <el-table-column label="所属行业" min-width="130" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.industryName || '-' }}</template>
            </el-table-column>
            <el-table-column label="地区" width="120" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ [scope.row.province, scope.row.city].filter(Boolean).join(' / ') || '-' }}
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
                v-if="portraitTotal > 0"
                small
                layout="total, prev, pager, next"
                :total="portraitTotal"
                :page-size="10"
                :current-page.sync="portraitPageNo"
                @current-change="fetchPortrait"
                class="portrait-pager" />
        </div>
      </div>
    </template>

    <!-- 第四个 tab：产业链图谱（占位） -->
    <template v-else>
      <div class="content-area">
        <div class="empty-content">
          <div class="empty-icon-wrapper">
            <div class="empty-icon">
              <svg viewBox="0 0 1024 1024" width="80" height="80">
                <path d="M128 64h768a64 64 0 0 1 64 64v768a64 64 0 0 1-64 64H128a64 64 0 0 1-64-64V128a64 64 0 0 1 64-64z m32 96v640h704V160H160z m96 160h192v192H256V320z m320 0h192v192H576V320z m-320 320h192v96H256v-96z m320 0h192v96H576v-96z" fill="#fff"/>
              </svg>
            </div>
          </div>
          <div class="empty-title">{{ currentSubTabLabel }}</div>
          <div class="empty-desc">功能建设中，敬请期待</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { searchEnterpriseByName, getEnterpriseBasicInfo, getEnterpriseShareholders, getEnterpriseBids, getEnterpriseBidDetail, getEnterpriseByMultiCondition } from '@/api/agents/agent'

export default {
  name: 'SmartDashboard',
  data() {
    return {
      activeSubTab: 'perspective',
      queryForm: {
        companyName: ''
      },
      // 模糊搜索选中的企业ID
      selectedEnterpriseId: '',
      // 企业基本信息
      enterpriseBasicInfo: null,
      // 详情loading
      detailLoading: false,
      // 股东
      shareholders: [],
      shareholderTotal: 0,
      shareholderTotalPages: 1,
      shareholderPageNo: 1,
      shareholderLoading: false,
      // 招投标查询
      bidForm: {
        companyName: '',
        enterpriseId: '',
        region: '',
        titleKeyword: '',
        projectKeyword: '',
        bidType: 'all',
        startDate: null,
        endDate: null
      },
      bids: [],
      bidTotal: 0,
      bidTotalPages: 1,
      bidPageNo: 1,
      bidLoading: false,
      bidResolving: false,
      bidQueried: false,
      // 公告详情（下钻）
      bidDetailView: false,
      bidDetail: null,
      bidDetailLoading: false,
      // AI 速读流式输出 loading（仅用于按钮状态展示，实际流式状态由父组件的抽屉控制）
      aiBriefLoading: false,
      // 招投标 AI 分析按钮 loading
      bidBriefLoading: false,
      // 企业画像筛选（条件树）
      portraitTree: {
        groups: [{
          id: 'root',
          logic: 'all', // all=AND / any=OR
          conditions: [{ id: 'rootc1', field: '', op: 'eq', value: null }]
        }]
      },
      portraitFieldSchema: [
        { key: 'enterpriseName', label: '企业名称', type: 'text', ops: ['contains', 'eq'] },
        { key: 'enterpriseStatus', label: '经营状态', type: 'select', multiple: true,
          options: ['存续', '在业', '注销', '吊销', '停业'], ops: ['in'] },
        { key: 'enterpriseType', label: '企业类型', type: 'select', multiple: true,
          options: ['有限责任公司', '股份有限公司', '国有企业', '外商投资', '合伙企业', '个体工商户'], ops: ['in'] },
        { key: 'industryName', label: '所属行业', type: 'text', ops: ['contains', 'eq'] },
        { key: 'province', label: '省份', type: 'select', multiple: true,
          options: ['北京市', '上海市', '广东省', '江苏省', '浙江省', '山东省', '四川省', '湖北省', '湖南省', '安徽省', '河北省', '河南省', '福建省', '天津市', '重庆市'], ops: ['in'] },
        { key: 'city', label: '城市', type: 'text', ops: ['contains', 'eq'] },
        { key: 'registeredCapital', label: '注册资本(万元)', type: 'number-range', ops: ['range'] },
        { key: 'establishDate', label: '成立日期', type: 'date-range', ops: ['range'] },
        { key: 'legalPersonName', label: '法定代表人', type: 'text', ops: ['eq'] }
      ],
      portraitList: [],
      portraitTotal: 0,
      portraitTotalPages: 1,
      portraitPageNo: 1,
      portraitLoading: false,
      portraitQueried: false,
      subTabs: [
        { key: 'perspective', label: '企业多维透视', icon: 'el-icon-s-grid' },
        { key: 'bidding', label: '招投标查询', icon: 'el-icon-document-copy' },
        { key: 'portrait', label: '企业画像筛选', icon: 'el-icon-user' },
        { key: 'chain', label: '产业链图谱', icon: 'el-icon-share' }
      ]
    }
  },
  computed: {
    currentSubTabLabel() {
      const tab = this.subTabs.find(t => t.key === this.activeSubTab)
      return tab ? tab.label : ''
    },
    // 招标类型显示文案
    bidTypeLabel() {
      const map = { all: '全部', 100: '招标方', 200: '投标方', 300: '代理方', 400: '被提及' }
      return map[this.bidForm.bidType] || '全部'
    },
    // 拟建/跨标项目数量
    bidBuiltCount() {
      return this.bids.filter(b => (b.type || '').includes('拟建')).length
    },
    // 前端二次过滤（日期区间 + 项目名称关键词）
    displayBids() {
      let list = this.bids
      const { startDate, endDate, projectKeyword } = this.bidForm
      if (startDate) {
        list = list.filter(b => (Number(b.publishTime) || 0) * 1000 >= startDate)
      }
      if (endDate) {
        list = list.filter(b => (Number(b.publishTime) || 0) * 1000 <= endDate + 86399999)
      }
      const kw = (projectKeyword || '').trim()
      if (kw) {
        list = list.filter(b => this.stripHtml(b.title || '').includes(kw))
      }
      return list
    },
    // 曾用名
    historyNames() {
      const list = this.enterpriseBasicInfo?.historyNames
      return Array.isArray(list) ? list : []
    },
    // 卡片数据（全部取自真实接口字段）
    statCards() {
      if (!this.enterpriseBasicInfo) return []
      const info = this.enterpriseBasicInfo
      return [
        { label: '注册资本', value: this.formatCapital(info.registeredCapital), foot: '公开披露口径' },
        { label: '参保人数', value: info.insuredCount || '--', foot: info.insuredCountSource || '以公开数据源为准' },
        { label: '成立年限', value: this.formatYears(info.establishDate), foot: info.establishDate ? info.establishDate.substring(0, 7).replace('-', '年') + '月成立' : '-' },
        { label: '员工规模', value: info.staffNumber || '--', foot: info.staffNumber ? '工商公示口径' : '暂无公开数据' },
        { label: '年营业额', value: info.annualTurnover || '--', foot: info.annualTurnover ? '公开披露口径' : '暂无公开数据' },
        { label: '法定代表人', value: info.legalPersonName || '--', foot: '登记机关：' + (info.issuingAuthority || '-') }
      ]
    }
  },
  methods: {
    /**
     * 解析后端 Paging<T> 响应为统一分页结构
     * 后端可能字段名：data/list/records，total/totalCount/count，totalPages/pages/pageCount
     * 值可能是字符串（如后端用 String 存 total）
     */
    _parsePage(res) {
      const payload = res?.data || res || {}
      const list = payload.data || payload.list || payload.records || []
      const rawTotal = payload.total ?? payload.totalCount ?? payload.count ?? payload.totalElements
      const total = rawTotal === undefined || rawTotal === null ? list.length : Number(rawTotal) || list.length
      const rawPages = payload.totalPages ?? payload.pages ?? payload.pageCount ?? payload.totalPage
      const pageSize = Number(payload.pageSize) || 10
      const totalPages = rawPages !== undefined && rawPages !== null
        ? (Number(rawPages) || 1)
        : Math.max(1, Math.ceil(total / pageSize))
      return { list, total, totalPages, pageSize, payload }
    },
    // ===== ① 模糊搜索 =====
    async handleSearchSuggestions(queryString, cb) {
      const keyword = (queryString || '').trim()
      if (keyword.length < 2) {
        cb([])
        return
      }
      try {
        const res = await searchEnterpriseByName(keyword)
        const { list } = this._parsePage(res)
        const suggestions = list.map(item => ({
          ...item,
          // el-autocomplete 需要 value 字段作为输入回显兜底
          value: this.stripHtml(item.enterpriseName),
          region: [item.province, item.city].filter(Boolean).join(' ')
        }))
        cb(suggestions)
      } catch (error) {
        console.error('企业模糊搜索失败:', error)
        cb([])
      }
    },
    // 选中建议项：记录企业信息并自动加载详情
    handleSelectEnterprise(item) {
      this.selectedEnterpriseId = item.platformEnterpriseId || ''
      this.queryForm.companyName = this.stripHtml(item.enterpriseName)
      this.loadEnterpriseDetail(this.queryForm.companyName)
    },
    handleClearResult() {
      this.selectedEnterpriseId = ''
      this.enterpriseBasicInfo = null
      this.shareholders = []
    },

    // ===== ② 企业详情精确搜索 =====
    handleQuery() {
      const name = (this.queryForm.companyName || '').trim()
      if (!name) {
        this.$message.warning('请输入企业名称')
        return
      }
      this.loadEnterpriseDetail(name)
    },
    async loadEnterpriseDetail(enterpriseName) {
      this.detailLoading = true
      this.enterpriseBasicInfo = null
      this.shareholders = []
      this.shareholderPageNo = 1
      try {
        const res = await getEnterpriseBasicInfo(enterpriseName)
        const detail = res?.data
        if (detail && (detail.platformEnterpriseId || detail.enterpriseName)) {
          this.enterpriseBasicInfo = detail
          // ③ 查询股东信息
          const entId = detail.platformEnterpriseId || this.selectedEnterpriseId
          if (entId) {
            this.fetchShareholders(1, entId)
          }
        } else {
          this.$message.info('未查询到该企业信息，请检查企业名称是否准确')
        }
      } catch (error) {
        console.error('企业详情查询失败:', error)
      } finally {
        this.detailLoading = false
      }
    },

    // ===== ③ 股东信息 =====
    async fetchShareholders(pageNo = 1, enterpriseId = '') {
      const entId = enterpriseId || this.enterpriseBasicInfo?.platformEnterpriseId || this.selectedEnterpriseId
      if (!entId) return
      this.shareholderLoading = true
      this.shareholderPageNo = pageNo
      try {
        const res = await getEnterpriseShareholders(entId, { pageNo, pageSize: 10 })
        const { list, total, totalPages } = this._parsePage(res)
        this.shareholders = list
        this.shareholderTotal = total
        this.shareholderTotalPages = totalPages
      } catch (error) {
        console.error('股东信息查询失败:', error)
        this.shareholders = []
      } finally {
        this.shareholderLoading = false
      }
    },

    // ===== 招投标查询 =====
    // 选中联想项：记录企业ID并自动查询
    handleBidSelect(item) {
      this.bidForm.enterpriseId = item.platformEnterpriseId || ''
      this.bidForm.companyName = this.stripHtml(item.enterpriseName)
      this.handleBidQuery()
    },
    handleBidClear() {
      this.bidForm.enterpriseId = ''
      this.bids = []
      this.bidTotal = 0
      this.bidTotalPages = 1
      this.bidQueried = false
      this.bidDetailView = false
      this.bidDetail = null
    },
    // 查询入口：先解析企业ID，再查招投标分页
    async handleBidQuery() {
      const name = (this.bidForm.companyName || '').trim()
      if (!name) {
        this.$message.warning('请输入企业名称')
        return
      }
      // 未从下拉选中时，用精确查询接口解析企业ID
      if (!this.bidForm.enterpriseId) {
        this.bidResolving = true
        try {
          const res = await getEnterpriseBasicInfo(name)
          const entId = res?.data?.platformEnterpriseId || ''
          if (!entId) {
            this.$message.info('未查询到该企业，请从下拉列表中选择')
            return
          }
          this.bidForm.enterpriseId = entId
        } catch (error) {
          console.error('企业ID解析失败:', error)
          return
        } finally {
          this.bidResolving = false
        }
      }
      this.bidPageNo = 1
      this.fetchBids()
    },
    // 招投标分页查询（招标类型=全部时，4种角色并行查询后合并）
    async fetchBids() {
      const entId = this.bidForm.enterpriseId
      if (!entId) return
      this.bidLoading = true
      this.bidDetailView = false
      try {
        const types = this.bidForm.bidType === 'all' ? [100, 200, 300, 400] : [this.bidForm.bidType]
        // all 模式：每个角色各取一页，合并后前端展示；单角色：直接后端分页
        const pageSizePerRole = this.bidForm.bidType === 'all' ? 20 : 10
        const results = await Promise.all(
          types.map(type =>
            getEnterpriseBids({
              enterpriseId: entId,
              type,
              titleKeyword: (this.bidForm.titleKeyword || '').trim() || undefined,
              pageNo: this.bidForm.bidType === 'all' ? 1 : this.bidPageNo,
              pageSize: pageSizePerRole
            }).catch(() => null)
          )
        )
        let list = []
        let total = 0
        results.forEach((res, idx) => {
          const { list: pageList, total: roleTotal } = this._parsePage(res)
          const typeVal = types[idx]
          pageList.forEach(row => { row.__type = typeVal })
          list = list.concat(pageList)
          total += roleTotal
        })
        // 按发布时间倒序
        list.sort((a, b) => (Number(b.publishTime) || 0) - (Number(a.publishTime) || 0))
        this.bids = list
        this.bidTotal = total
        // totalPages：all 模式用合并后计算，单角色用 Math.ceil
        this.bidTotalPages = Math.max(1, Math.ceil(total / 10))
        this.bidQueried = true
      } finally {
        this.bidLoading = false
      }
    },
    handleBidReset() {
      this.bidForm = {
        companyName: '',
        enterpriseId: '',
        region: '',
        titleKeyword: '',
        projectKeyword: '',
        bidType: 'all',
        startDate: null,
        endDate: null
      }
      this.bids = []
      this.bidTotal = 0
      this.bidTotalPages = 1
      this.bidPageNo = 1
      this.bidQueried = false
      this.bidDetailView = false
      this.bidDetail = null
    },
    // 公告状态标签颜色
    bidStatusTag(type) {
      const t = type || ''
      if (t.includes('中标')) return 'success'
      if (t.includes('拟建')) return 'warning'
      if (t.includes('招标')) return ''
      return 'info'
    },
    // 点击公告标题下钻到详情
    async handleBidDrill(row) {
      const type = row.__type
      const companyId = this.bidForm.enterpriseId
      if (!row.id || !type || !companyId) {
        this.$message.warning('该公告缺少详情参数，无法查看详情')
        return
      }
      this.bidDetailView = true
      this.bidDetailLoading = true
      this.bidDetail = null
      try {
        const res = await getEnterpriseBidDetail({
          id: row.id,
          type,
          companyId,
          platformCode: row.platformCode || undefined
        })
        if (res?.data && res.success !== false) {
          this.bidDetail = res.data
        } else {
          this.$message.error('详情查询失败')
          this.bidDetailView = false
        }
      } catch (error) {
        console.error('招投标详情查询失败:', error)
        this.bidDetailView = false
      } finally {
        this.bidDetailLoading = false
      }
    },
    // 金额格式化（接口单位为万元）
    formatMoney(val) {
      if (val === null || val === undefined || val === '') return '-'
      const num = parseFloat(val)
      if (isNaN(num) || num === 0) return '未公开'
      if (Math.abs(num) >= 10000) {
        return (num / 10000).toFixed(4) + '亿元'
      }
      return num.toLocaleString('zh-CN', { maximumFractionDigits: 2 }) + '万元'
    },
    // ===== 企业画像筛选（高级条件构造器） =====
    _pid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6) },
    // ---------- 字段 schema 辅助 ----------
    fieldType(key) {
      return (this.portraitFieldSchema.find(f => f.key === key) || {}).type || 'text'
    },
    fieldLabel(key) {
      return (this.portraitFieldSchema.find(f => f.key === key) || {}).label || ''
    },
    fieldOptions(key) {
      return (this.portraitFieldSchema.find(f => f.key === key) || {}).options || []
    },
    isMultiple(key) {
      return !!(this.portraitFieldSchema.find(f => f.key === key) || {}).multiple
    },
    fieldOps(key) {
      const schema = this.portraitFieldSchema.find(f => f.key === key) || { ops: ['eq'] }
      const labelMap = { eq: '等于', contains: '包含任意一个', in: '包含任意一个', range: '介于', gte: '大于等于', lte: '小于等于', nin: '不包含任意一个', neq: '不等于' }
      return (schema.ops || ['eq']).map(op => ({ value: op, label: labelMap[op] || op }))
    },
    // ---------- 操作行/组 ----------
    addGroup() {
      this.portraitTree.groups.push({
        id: this._pid(),
        logic: 'all',
        conditions: [{ id: this._pid(), field: '', op: 'eq', value: null }]
      })
    },
    removeGroup(gIdx) {
      if (this.portraitTree.groups.length <= 1) {
        this.$message.warning('至少保留一个条件组')
        return
      }
      this.portraitTree.groups.splice(gIdx, 1)
    },
    addCond(gIdx) {
      this.portraitTree.groups[gIdx].conditions.push({
        id: this._pid(),
        field: '',
        op: 'eq',
        value: null
      })
    },
    removeCond(gIdx, cIdx) {
      const conds = this.portraitTree.groups[gIdx].conditions
      if (conds.length <= 1) {
        // 最后一行清空而不是删除整组
        conds[cIdx] = { id: this._pid(), field: '', op: 'eq', value: null }
      } else {
        conds.splice(cIdx, 1)
      }
    },
    onFieldChange(cond) {
      // 切换字段时重置值和操作符
      const ops = this.fieldOps(cond.field)
      cond.op = ops[0]?.value || 'eq'
      if (this.fieldType(cond.field) === 'number-range' || this.fieldType(cond.field) === 'date-range') {
        cond.value = null
        cond.valueMin = ''
        cond.valueMax = ''
      } else if (this.fieldType(cond.field) === 'select' && this.isMultiple(cond.field)) {
        cond.value = []
      } else {
        cond.value = ''
      }
    },
    // ---------- 校验 & 构建 ----------
    // 校验条件树并返回可用于后端的鲸海 condition JSON 字符串；null 表示校验失败
    buildPortraitCondition() {
      const must = []
      for (const group of this.portraitTree.groups) {
        const list = []
        for (const cond of group.conditions) {
          if (!cond.field) continue
          const item = this.buildLeaf(cond)
          if (!item) return null // 某条件未填全
          list.push(item)
        }
        if (!list.length) return null
        if (group.logic === 'all') {
          must.push(list) // 鲸海 must 内层为数组 → AND
        } else {
          must.push({ $or: list }) // should → OR
        }
      }
      if (!must.length) return null
      // 扁平化：如果所有组都是 all 且只有一个条件组，直接展平
      const allSingle = must.every(g => Array.isArray(g)) && must.length === 1
      const mustFinal = allSingle ? must[0] : must
      return JSON.stringify({ must: mustFinal })
    },
    // 构建单条件叶子对象（鲸海原生）
    buildLeaf(cond) {
      const t = this.fieldType(cond.field)
      if (t === 'number-range' || t === 'date-range') {
        const range = {}
        if (cond.valueMin) {
          const n = Number(cond.valueMin)
          range.gte = isNaN(n) ? cond.valueMin : n
        }
        if (cond.valueMax) {
          const n = Number(cond.valueMax)
          range.lte = isNaN(n) ? cond.valueMax : n
        }
        if (Object.keys(range).length === 0) return null
        return { [cond.field]: [range] }
      }
      // 常规：select 多选用 in，单选或 text 用 eq / contains→in
      let op = cond.op
      if (op === 'contains') op = 'in'
      let val = cond.value
      if (val === null || val === undefined || val === '') return null
      if (Array.isArray(val) && val.length === 0) return null
      if (op === 'in' && !Array.isArray(val)) val = [val]
      return { [cond.field]: [{ [op]: val }] }
    },
    handlePortraitQuery() {
      const empty = this.portraitTree.groups.every(g => g.conditions.every(c => !c.field))
      if (empty) {
        this.$message.warning('请至少添加一个有效条件')
        return
      }
      this.portraitPageNo = 1
      this.fetchPortrait()
    },
    async fetchPortrait() {
      const condition = this.buildPortraitCondition()
      if (!condition) {
        this.$message.warning('请完整填写条件的字段、操作符和值')
        return
      }
      this.portraitLoading = true
      try {
        const res = await getEnterpriseByMultiCondition({ condition, pageNo: this.portraitPageNo, pageSize: 10 })
        const { list, total, totalPages } = this._parsePage(res)
        this.portraitList = list
        this.portraitTotal = total
        this.portraitTotalPages = totalPages
        this.portraitQueried = true
      } catch (error) {
        console.error('企业画像筛选查询失败:', error)
        this.portraitList = []
        this.$message.error('查询失败，请检查条件是否正确')
      } finally {
        this.portraitLoading = false
      }
    },
    handlePortraitReset() {
      this.portraitTree = {
        groups: [{ id: this._pid(), logic: 'all', conditions: [{ id: this._pid(), field: '', op: 'eq', value: null }] }]
      }
      this.portraitList = []
      this.portraitTotal = 0
      this.portraitTotalPages = 1
      this.portraitPageNo = 1
      this.portraitQueried = false
    },
    // 经营状态标签颜色
    portraitStatusTag(status) {
      const s = status || ''
      if (s.includes('存续') || s.includes('在业')) return 'success'
      if (s.includes('注销')) return 'danger'
      if (s.includes('吊销') || s.includes('停业')) return 'warning'
      return 'info'
    },
    // 点击企业名称下钻到企业多维透视
    handlePortraitDrill(row) {
      if (!row.enterpriseName) return
      this.activeSubTab = 'perspective'
      this.queryForm.companyName = this.stripHtml(row.enterpriseName)
      this.selectedEnterpriseId = row.platformEnterpriseId || ''
      this.loadEnterpriseDetail(this.queryForm.companyName)
    },
    // 导出筛选结果
    handlePortraitExport() {
      if (!this.portraitList.length) {
        this.$message.warning('暂无可导出的数据')
        return
      }
      const header = ['企业名称', '法定代表人', '注册资本', '成立日期', '经营状态', '企业类型', '所属行业', '省份', '城市', '统一社会信用代码']
      const rows = this.portraitList.map(r => [
        this.stripHtml(r.enterpriseName), r.legalPersonName || '', r.registeredCapital || '',
        r.establishDate || '', r.enterpriseStatus || '', r.enterpriseType || '',
        r.industryName || '', r.province || '', r.city || '', r.creditCode || ''
      ])
      const csv = [header, ...rows].map(r => r.map(c => '"' + String(c ?? '').replace(/"/g, '""') + '"').join(',')).join('\r\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `企业画像筛选结果_${this.portraitTotal}家.csv`
      link.click()
      URL.revokeObjectURL(link.href)
    },

    handleReset() {
      this.queryForm.companyName = ''
      this.selectedEnterpriseId = ''
      this.enterpriseBasicInfo = null
      this.shareholders = []
      this.shareholderPageNo = 1
    },

    // ===== 工具方法 =====
    // 去除搜索结果中的 <strong> 高亮标签
    stripHtml(html) {
      return (html || '').replace(/<[^>]+>/g, '')
    },
    // 注册资本：万元 ≥ 10000 时转换为亿元
    formatCapital(capital) {
      if (!capital) return '--'
      const match = String(capital).match(/([\d.]+)\s*万/)
      if (!match) return capital
      const wan = parseFloat(match[1])
      if (!isNaN(wan) && wan >= 10000) {
        return (wan / 10000).toFixed(2) + '亿元'
      }
      return capital
    },
    // ===== AI 速读（场景 A：企业多维透视） =====
    // 组装企业工商 + 股东数据为上下文对象
    buildEnterpriseBriefContext() {
      const info = this.enterpriseBasicInfo || {}
      const top = (this.shareholders || []).slice(0, 5).map(s => ({
        name: this.stripHtml(s.shareholderName || ''),
        ratio: s.shareholdingRatio || '',
        type: s.shareholderType || '',
        subscribed: s.subscribedCapital || ''
      }))
      return {
        enterpriseName: this.stripHtml(info.enterpriseName || ''),
        creditCode: info.creditCode || '',
        enterpriseType: info.enterpriseType || '',
        legalPersonName: info.legalPersonName || '',
        registeredCapital: info.registeredCapital || '',
        establishDate: info.establishDate || '',
        enterpriseStatus: info.enterpriseStatus || '',
        industryName: info.industryName || '',
        issuingAuthority: info.issuingAuthority || '',
        businessPeriod: info.businessPeriod || '',
        insuredCount: info.insuredCount || '',
        enterpriseProfile: info.enterpriseProfile || '',
        businessScope: info.businessScope || '',
        shareholderTotal: this.shareholderTotal || 0,
        topShareholders: top
      }
    },
    // 拼接 prompt：用简洁文本替代 JSON 代码块，突出企业名称
    buildEnterpriseBriefPrompt(ctx) {
      const name = ctx.enterpriseName || '该企业'
      const lines = [
        `请基于以下【${name}】的工商数据与股东信息，生成一份《企业速读》分析。`,
        '输出要求：',
        '1. 一句话企业定位（所在行业 + 市场地位推断）；',
        '2. 股权结构特征（3 条以内：集中度/穿透/疑似关联方）；',
        '3. 经营风险提示（3 条以内：成立年限、经营状态、参保人数等）；',
        '4. 行业地位推断与关注点（2 条以内）。',
        '请严格使用中文，输出格式使用 Markdown，总长度不超过 600 字。',
        '',
        '【企业工商数据】',
        `企业名称：${name}`,
        ctx.creditCode ? `统一社会信用代码：${ctx.creditCode}` : '',
        ctx.enterpriseType ? `企业类型：${ctx.enterpriseType}` : '',
        ctx.legalPersonName ? `法定代表人：${ctx.legalPersonName}` : '',
        ctx.registeredCapital ? `注册资本：${ctx.registeredCapital}` : '',
        ctx.establishDate ? `成立日期：${ctx.establishDate}` : '',
        ctx.enterpriseStatus ? `经营状态：${ctx.enterpriseStatus}` : '',
        ctx.industryName ? `所属行业：${ctx.industryName}` : '',
        ctx.insuredCount ? `参保人数：${ctx.insuredCount}` : '',
        ctx.businessPeriod ? `营业期限：${ctx.businessPeriod}` : '',
        ctx.enterpriseProfile ? `企业简介：${ctx.enterpriseProfile}` : '',
        ctx.businessScope ? `经营范围：${ctx.businessScope}` : '',
        '',
        '【股东信息】',
        `股东总数：${ctx.shareholderTotal || 0}`,
        ...(ctx.topShareholders || []).map((s, i) =>
          `  ${i + 1}. ${s.name || '未知'}（持股 ${s.ratio || '未知'}，${s.type || '类型未知'}${s.subscribed ? `，认缴 ${s.subscribed}` : ''}）`
        )
      ]
      return lines.filter(l => l !== '').join('\n')
    },
    // 触发：组上下文 → 拼 prompt → emit 给父组件 EnterpriseListNew
    handleEnterpriseBriefAi() {
      const info = this.enterpriseBasicInfo
      if (!info || !info.enterpriseName) {
        this.$message.warning('请先查询一家企业')
        return
      }
      const ctx = this.buildEnterpriseBriefContext()
      const content = this.buildEnterpriseBriefPrompt(ctx)
      this.aiBriefLoading = true
      // 1.5 秒后自动恢复按钮状态（父组件抽屉打开后会自行管理流式状态）
      setTimeout(() => { this.aiBriefLoading = false }, 1500)
      this.$emit('smart-analysis', {
        type: 'enterpriseWarRoom',
        scene: 'enterprise_brief',
        content
      })
    },
    // ========== 招投标智能分析 ==========
    // 组装招投标上下文：角色分布、类型分布、采购单位 Top5、月度趋势、渠道分布、代表性公告
    buildBidBriefContext() {
      const bids = this.bids || []
      const entName = this.stripHtml(this.enterpriseBasicInfo?.enterpriseName || '') || this.bidForm.companyName || '该企业'
      const total = this.bidTotal || bids.length

      // 角色分布
      const roleMap = {}
      bids.forEach(b => {
        const r = (b.role || '未知').trim()
        roleMap[r] = (roleMap[r] || 0) + 1
      })

      // 公告类型分布
      const typeMap = {}
      bids.forEach(b => {
        const t = (b.type || '未知').trim()
        typeMap[t] = (typeMap[t] || 0) + 1
      })

      // 采购单位 Top5
      const ownerMap = {}
      bids.forEach(b => {
        const o = this.stripHtml(b.owner || '').trim()
        if (o) ownerMap[o] = (ownerMap[o] || 0) + 1
      })
      const topOwners = Object.entries(ownerMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }))

      // 月度趋势（最近 6 个月）
      const now = new Date()
      const monthMap = {}
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        monthMap[key] = 0
      }
      bids.forEach(b => {
        const ts = (Number(b.publishTime) || 0) * 1000
        if (!ts) return
        const d = new Date(ts)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (key in monthMap) monthMap[key]++
      })
      const monthlyTrend = Object.entries(monthMap).map(([month, count]) => ({ month, count }))

      // 发布渠道分布
      const channelMap = {}
      bids.forEach(b => {
        (b.comeFromArr || []).forEach(c => {
          const ch = (c || '').trim()
          if (ch) channelMap[ch] = (channelMap[ch] || 0) + 1
        })
      })
      const channels = Object.entries(channelMap)
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => `${name}(${count})`)

      // 代表性公告 5 条
      const repBids = bids.slice(0, 5).map(b => ({
        title: this.stripHtml(b.title || ''),
        owner: this.stripHtml(b.owner || ''),
        role: b.role || '',
        type: b.type || '',
        date: this.formatDate(b.publishTime)
      }))

      return {
        enterpriseName: entName,
        total,
        currentPageSize: bids.length,
        roleDistribution: Object.entries(roleMap).map(([role, count]) => ({ role, count })),
        typeDistribution: Object.entries(typeMap).map(([type, count]) => ({ type, count })),
        topOwners,
        monthlyTrend,
        channels,
        representativeBids: repBids
      }
    },
    // 拼接 prompt：简洁文本格式，突出企业名称
    buildBidBriefPrompt(ctx) {
      const name = ctx.enterpriseName
      const lines = [
        `请基于以下【${name}】的招投标公开记录数据，生成一份《招投标智能分析》，输出要求如下：`,
        '1. 一句话招投标活跃度定位（活跃/低活跃/沉寂 + 简述原因）；',
        '2. 企业角色特征（3 条以内：偏招标方还是投标方、是否中标主力、上下游关系推断）；',
        '3. 主要采购单位关系（3 条以内：Top 采购单位分析、集中度、合作稳定性）；',
        '4. 风险与机会提示（2 条以内：客户集中度风险、新机会/新渠道）。',
        '请严格使用中文，输出格式使用 Markdown，总长度不超过 600 字。',
        '',
        '【招投标总览】',
        `企业名称：${name}`,
        `公告总数：${ctx.total} 条（当前页加载 ${ctx.currentPageSize} 条）`,
        '',
        '【企业角色分布】',
        ...ctx.roleDistribution.map(r => `  ${r.role}：${r.count} 次`),
        '',
        '【公告类型分布】',
        ...ctx.typeDistribution.map(t => `  ${t.type}：${t.count} 条`),
        '',
        '【主要采购单位 Top 5】',
        ...(ctx.topOwners.length > 0
          ? ctx.topOwners.map(o => `  ${o.name}：${o.count} 次`)
          : ['  无数据']),
        '',
        '【近 6 个月公告数趋势】',
        ...ctx.monthlyTrend.map(m => `  ${m.month}：${m.count} 条`),
        '',
        '【发布渠道分布】',
        ctx.channels.length > 0 ? `  ${ctx.channels.join('、')}` : '  无数据',
        '',
        '【代表性公告（前 5 条）】',
        ...ctx.representativeBids.map((b, i) =>
          `  ${i + 1}. ${b.title || '无标题'} | 采购单位：${b.owner || '-'} | 角色：${b.role || '-'} | 类型：${b.type || '-'} | 时间：${b.date}`
        )
      ]
      return lines.join('\n')
    },
    // 招投标 AI 分析入口
    handleBidBriefAi() {
      const bids = this.bids || []
      if (bids.length === 0) {
        this.$message.warning('暂无招投标数据，无法分析')
        return
      }
      const ctx = this.buildBidBriefContext()
      const content = this.buildBidBriefPrompt(ctx)
      this.bidBriefLoading = true
      setTimeout(() => { this.bidBriefLoading = false }, 1500)
      this.$emit('smart-analysis', {
        type: 'enterpriseWarRoom',
        scene: 'bid_brief',
        content
      })
    },
    // 成立年限
    formatYears(establishDate) {
      if (!establishDate) return '--'
      const start = new Date(establishDate.replace(/-/g, '/'))
      if (isNaN(start.getTime())) return '--'
      const now = new Date()
      let years = now.getFullYear() - start.getFullYear()
      const m = now.getMonth() - start.getMonth()
      if (m < 0 || (m === 0 && now.getDate() < start.getDate())) years--
      return years > 0 ? years + '年+' : '不足1年'
    },
    // Unix 秒时间戳 → yyyy-MM-dd
    formatDate(ts) {
      if (ts === null || ts === undefined || ts === '') return '-'
      const n = Number(ts)
      if (isNaN(n) || n <= 0) return '-'
      const d = new Date(n * 1000)
      if (isNaN(d.getTime())) return '-'
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mm}-${dd}`
    }
  }
}
</script>

<style lang="scss" scoped>
.smart-dashboard {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
}

/* 二级 Tab */
.sub-tabs {
  display: flex;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 4px;
  flex-shrink: 0;
}

.sub-tab-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    color: #4A5FF3;
  }

  &.active {
    background: #fff;
    color: #4A5FF3;
    box-shadow: 0 2px 8px rgba(74, 95, 243, 0.15);
    font-weight: 600;
  }

  i {
    font-size: 16px;
  }
}

/* 查询面板 */
.query-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  flex-shrink: 0;
}

.query-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.query-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  font-weight: 600;

  i {
    color: #4A5FF3;
  }
}

.required-tip {
  font-size: 12px;
  color: #999;
  font-weight: 400;
  margin-left: 4px;
}

.query-actions {
  display: flex;
  gap: 8px;
}

.query-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 2px;
}

.required {
  color: #f5222d;
}

.form-input {
  max-width: 100%;
}

/* 搜索建议项 */
.suggestion-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  width: 100%;
}

.suggestion-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.suggestion-name {
  font-size: 14px;
  color: #333;

  ::v-deep strong {
    color: #4A5FF3;
    font-weight: 600;
  }
}

.suggestion-region {
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
}

.suggestion-credit {
  font-size: 12px;
  color: #bbb;
  flex-shrink: 0;
}

/* 内容区域 */
.content-area {
  flex: 1;
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

/* 空状态 */
.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  background: #fff;
  border-radius: 8px;
}

.empty-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon {
  width: 88px;
  height: 88px;
  border-radius: 20px;
  background: linear-gradient(135deg, #4A5FF3 0%, #7B5CF7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(74, 95, 243, 0.3);
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-top: 8px;
}

.empty-desc {
  font-size: 13px;
  color: #999;
}

.dim-item {
  color: #4A5FF3;
}

.dim-sep {
  margin: 0 6px;
  color: #ddd;
}

/* 企业信息 */
.enterprise-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 头部卡片 */
.ent-header {
  background: linear-gradient(90deg, #f5f7ff 0%, #faf8ff 100%);
  border-radius: 10px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.ent-logo {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: linear-gradient(135deg, #4A5FF3 0%, #7B5CF7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 28px;
    color: #fff;
  }
}

.ent-head-main {
  flex: 1;
  min-width: 0;
}

.ent-name {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
}

.ent-sub {
  font-size: 13px;
  color: #999;
  margin-top: 4px;
}

.ent-status-tag {
  flex-shrink: 0;
  font-size: 13px;
  color: #52c41a;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 卡片数据 */
.stat-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

.stat-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 14px 16px;
}

.stat-label {
  font-size: 13px;
  color: #999;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 8px 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-foot {
  font-size: 12px;
  color: #bbb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 详情两栏：stretch 保证两张卡片等高 */
.detail-row {
  display: flex;
  gap: 16px;
  align-items: stretch;
}

.archive-section {
  flex: 1.15;
  min-width: 0;
}
.ent-actions {
  margin-left: auto;
  flex-shrink: 0;
}

.shareholder-section {
  flex: 1;
  min-width: 0;
}

.info-section {
  background: #fff;
  border-radius: 8px;
  padding: 18px 20px;
  border: 1px solid #f0f0f0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;

  i {
    color: #4A5FF3;
    font-size: 18px;
  }
}

.alive-tag {
  margin-left: auto;
  font-size: 12px;
  font-weight: 400;
  color: #52c41a;
  display: flex;
  align-items: center;
  gap: 4px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #52c41a;
  }
}

.section-tip {
  font-size: 12px;
  color: #999;
  margin: -8px 0 12px;
  flex-shrink: 0;
}

.shareholder-section .el-table {
  flex-shrink: 0;
}

.info-cell {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.7;
  margin-bottom: 10px;
}

.info-grid-two {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 20px;
}

.info-label {
  color: #999;
  flex-shrink: 0;
  min-width: 92px;

  &::after {
    content: '：';
  }
}

.info-value {
  color: #333;
  word-break: break-all;
  flex: 1;
}

.history-tag {
  display: inline-block;
  font-size: 12px;
  color: #666;
  background: #f5f7fa;
  border-radius: 4px;
  padding: 2px 8px;
  margin: 0 6px 4px 0;
}

.ratio-text {
  color: #4A5FF3;
  font-weight: 600;
}

.shareholder-pager {
  margin-top: auto;
  padding-top: 12px;
  text-align: right;
}

/* ===== 招投标查询 ===== */
.bid-form {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px 24px;
}

.bid-form .form-item {
  margin: 0;

  .el-input,
  .el-select,
  .el-autocomplete {
    width: 100%;
  }
}

.bid-date-item {
  grid-column: span 2;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-date-editor {
    flex: 1;
    width: auto;
  }
}

.date-sep {
  color: #999;
  flex-shrink: 0;
}

/* 跟踪头部 */
.bid-content {
  gap: 16px;
}

.bid-track-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.track-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #333;

  i {
    color: #52c41a;
    font-size: 18px;
  }
}

.track-actions {
  display: flex;
  gap: 8px;
}

/* 记录卡片 */
.bid-record-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px 20px;
}

.bid-record-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(90deg, #f5f7ff 0%, #faf8ff 100%);
  border-radius: 8px;
  margin-bottom: 16px;
}

.bid-record-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #4A5FF3 0%, #7B5CF7 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.bid-record-info {
  flex: 1;
  min-width: 0;
}

.bid-record-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
}

.bid-record-sub {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.bid-record-tags {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.bid-tag {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  white-space: nowrap;
}

.bid-tag-green {
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}

.bid-tag-orange {
  color: #d46b08;
  background: #fff7e6;
  border: 1px solid #ffd591;
}

/* 标题链接 */
.bid-title-link {
  color: #4A5FF3;
  cursor: pointer;

  i {
    margin-left: 4px;
    font-size: 12px;
  }

  &:hover {
    text-decoration: underline;
  }
}

.bid-pager {
  margin-top: 12px;
  text-align: right;
}

/* 招投标空状态 */
.bid-empty {
  min-height: 320px;
}

/* ===== 招投标详情（下钻） ===== */
.bid-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-back-row {
  flex-shrink: 0;
}

.detail-header {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 20px 24px;
}

.detail-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.5;
}

.detail-tags {
  display: flex;
  gap: 8px;
  margin: 12px 0 14px;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 28px;
  font-size: 13px;
  color: #888;

  i {
    color: #4A5FF3;
    margin-right: 4px;
  }
}

/* 金额卡片 */
.detail-amount-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.amount-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px 20px;
}

.amount-card-highlight {
  background: linear-gradient(135deg, #f5f7ff 0%, #faf8ff 100%);
  border-color: #d8defc;
}

.amount-label {
  font-size: 13px;
  color: #999;
}

.amount-value {
  font-size: 22px;
  font-weight: 700;
  color: #4A5FF3;
  margin-top: 8px;
}

.amount-card:not(.amount-card-highlight) .amount-value {
  color: #1a1a1a;
  font-size: 18px;
}

.amount-sub {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

/* 详情区块 */
.detail-section {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 18px 20px;
}

.detail-two-col {
  display: flex;
  gap: 16px;
  align-items: stretch;

  .detail-section {
    flex: 1;
    min-width: 0;
  }
}

/* 公告正文 */
.detail-text {
  font-size: 13px;
  line-height: 1.9;
  color: #555;
  white-space: pre-wrap;
  word-break: break-all;
  background: #fafbfc;
  border-radius: 6px;
  padding: 16px;
  max-height: 480px;
  overflow-y: auto;
}

.bid-source-link {
  color: #4A5FF3;
  margin-right: 14px;
  white-space: nowrap;

  i {
    font-size: 12px;
    margin-left: 2px;
  }

  &:hover {
    text-decoration: underline;
  }
}

/* ===== 企业画像筛选（高级条件构造器） ===== */
.portrait-panel {
  flex-shrink: 0;
}

/* 条件组 */
.cond-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cond-group {
  border: 1px solid #eef1f7;
  border-left: 3px solid #4A5FF3;
  border-radius: 6px;
  background: #fafbff;
  padding: 12px 14px;
}

.cond-group-root {
  border-left-color: #4A5FF3;
}

.cond-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e5e8f0;
}

.cond-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #555;

  i {
    color: #4A5FF3;
    font-size: 14px;
  }
}

.cond-logic-select {
  width: 110px;
}

.cond-colon {
  color: #999;
  font-size: 13px;
}

.cond-group-del {
  margin-left: auto;
  color: #ff4d4f;
}

/* 条件行 */
.cond-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cond-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #eef1f7;
  transition: border-color 0.2s;

  &:hover {
    border-color: #d8defc;
  }
}

.cond-field-select {
  width: 140px;
  flex-shrink: 0;
}

.cond-op-select {
  width: 130px;
  flex-shrink: 0;
}

.cond-value-input {
  flex: 1;
  min-width: 180px;
}

.cond-row-del {
  flex-shrink: 0;
  color: #bbb;

  &:hover {
    color: #ff4d4f;
  }
}

/* 值控件 */
.cond-range-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;

  .el-input,
  .el-date-editor {
    flex: 1;
    min-width: 0;
  }
}

.cond-range-input {
  width: 100px;
  flex-shrink: 0;
}

.range-sep-tile {
  color: #999;
  font-size: 12px;
  flex-shrink: 0;
}

.cond-range-unit {
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
}

/* 添加行 */
.cond-row-add {
  padding: 2px 4px;
}

/* 添加组按钮 */
.cond-add-group {
  display: flex;
  justify-content: flex-start;
  padding: 4px 4px 0;
}

/* 结果区 */
.portrait-content { gap: 16px; }

.portrait-result {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px 20px;
}

.portrait-result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .result-count {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #333;

    i { color: #4A5FF3; font-size: 18px; }
    strong { color: #4A5FF3; font-size: 18px; }
  }
}

.portrait-name-link {
  color: #4A5FF3;
  cursor: pointer;

  i { margin-left: 4px; font-size: 12px; }

  &:hover { text-decoration: underline; }
}

.portrait-pager {
  margin-top: 12px;
  text-align: right;
}
</style>
